import Api from "api/Api";
import axios from "axios";
import type { AxiosPromise, AxiosResponse } from "axios";
import type { ApiResponse } from "api/types";
import type { SSHKey, CreateSSHKeyParams } from "ee/types/sshKeysTypes";

export type CreateSSHKeyResponse = ApiResponse<SSHKey>;
export type FetchSSHKeysResponse = ApiResponse<SSHKey[]>;
export type DeleteSSHKeyResponse = ApiResponse<SSHKey>;
export type ImportSSHKeyResponse = ApiResponse<SSHKey>;
export type GetSSHKeyResponse = ApiResponse<SSHKey>;
export type ShareSSHKeyResponse = ApiResponse<SSHKey>;

export class SSHKeysApi {
  static baseUrl = "/v1/ssh-keys";

  static async createSSHKey(
    params: CreateSSHKeyParams,
  ): AxiosPromise<CreateSSHKeyResponse> {
    const queryParams: Record<string, string> = {};

    if (params.name) {
      queryParams.name = params.name;
    }

    if (params.keyType) {
      queryParams.keyType = params.keyType;
    }

    return Api.post(SSHKeysApi.baseUrl, undefined, queryParams);
  }

  static async fetchSSHKeys(): AxiosPromise<FetchSSHKeysResponse> {
    return Api.get(SSHKeysApi.baseUrl);
  }

  static async fetchOwnedSSHKeys(): AxiosPromise<FetchSSHKeysResponse> {
    return Api.get(`${SSHKeysApi.baseUrl}/owned`);
  }

  static async fetchSharedSSHKeys(): AxiosPromise<FetchSSHKeysResponse> {
    return Api.get(`${SSHKeysApi.baseUrl}/shared`);
  }

  static async getSSHKey(id: string): AxiosPromise<GetSSHKeyResponse> {
    return Api.get(`${SSHKeysApi.baseUrl}/${id}`);
  }

  static async deleteSSHKey(id: string): AxiosPromise<DeleteSSHKeyResponse> {
    return Api.delete(`${SSHKeysApi.baseUrl}/${id}`);
  }

  static async importSSHKey(file: File): AxiosPromise<ImportSSHKeyResponse> {
    const formData = new FormData();

    formData.append("file", file);

    return Api.post(`${SSHKeysApi.baseUrl}/import`, formData, undefined, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  // Uses axios directly instead of the Api class because the Api class's
  // success interceptor unwraps `response.data`, which loses access to
  // response headers needed for the filename.
  static async exportSSHKey(id: string): Promise<AxiosResponse<Blob>> {
    return axios.get(`/api${SSHKeysApi.baseUrl}/${id}/export`, {
      responseType: "blob",
      withCredentials: true,
    });
  }

  static async shareSSHKey(
    id: string,
    emails: string[],
  ): AxiosPromise<ShareSSHKeyResponse> {
    return Api.post(`${SSHKeysApi.baseUrl}/${id}/share`, emails);
  }

  static async revokeSSHKey(
    id: string,
    emails: string[],
  ): AxiosPromise<ShareSSHKeyResponse> {
    return Api.post(`${SSHKeysApi.baseUrl}/${id}/revoke`, emails);
  }
}

export default SSHKeysApi;
